import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import getAPIUrl from "./API_FIX";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Check if BACKEND_URL already contains /api to avoid duplication
const API = getAPIUrl(BACKEND_URL);

const ShooterDetail = () => {
  const { shooterId } = useParams();
  const { isAdmin } = useAuth();
  const [shooter, setShooter] = useState(null);
  const [report, setReport] = useState(null);
  const [averages, setAverages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCaliberTab, setSelectedCaliberTab] = useState(null);
  const [selectedYear, setSelectedYear] = useState("all");
  const [availableYears, setAvailableYears] = useState([]);
  
  // Edit mode states
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({
    name: "",
    nra_number: "",
    cmp_number: "",
    rating: "",
    caliber_group: "Open",
    competitor_number: "",
    division: "Civilian",
    special_categories: [],
  });

  const SPECIAL_OPTIONS = [
    "Grand Senior",
    "Senior",
    "Women",
    "Veteran",
    "Service and Police",
  ];
  // Orthogonal add-ons that may combine with any age category (and each other).
  const COMBINABLE_SPECIALS = ["Women", "Service and Police"];

  const toggleSpecial = (list, value) => {
    const has = list.includes(value);
    if (has) return list.filter((x) => x !== value);
    if (COMBINABLE_SPECIALS.includes(value)) return [...list, value];
    const kept = list.filter((x) => COMBINABLE_SPECIALS.includes(x));
    return [...kept, value];
  };

  useEffect(() => {
    const fetchShooterData = async () => {
      try {
        // Get the token from localStorage
        const token = localStorage.getItem('token');
        if (!token) {
          setError("Authentication required. Please log in again.");
          setLoading(false);
          return;
        }

        // Set authorization header
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };
        
        // Fetch shooter details
        const shooterResponse = await axios.get(`${API}/shooters/${shooterId}`, config);
        setShooter(shooterResponse.data);
        
        // Fetch shooter report
        const reportResponse = await axios.get(`${API}/shooter-report/${shooterId}`, config);
        setReport(reportResponse.data);
        
        // Extract unique years from match dates
        if (reportResponse.data && reportResponse.data.matches) {
          const years = [...new Set(Object.values(reportResponse.data.matches).map(match => 
            new Date(match.match.date).getFullYear()
          ))].sort((a, b) => b - a); // Sort years in descending order
          
          setAvailableYears(years);
          
          // Set selected year to the most recent year if available
          if (years.length > 0) {
            setSelectedYear(years[0].toString());
          }
        }
        
        // Fetch shooter averages for the caliber tabs
        const averagesResponse = await axios.get(`${API}/shooter-averages/${shooterId}`, config);
        setAverages(averagesResponse.data);
        
        // Set default caliber tab if available
        if (averagesResponse.data && averagesResponse.data.caliber_averages) {
          const calibers = Object.keys(averagesResponse.data.caliber_averages);
          if (calibers.length > 0) {
            setSelectedCaliberTab(calibers[0]);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching shooter details:", err);
        setError("Failed to load shooter details. Please try again.");
        setLoading(false);
      }
    };

    fetchShooterData();
  }, [shooterId]);

  // Handle editing shooter info
  const handleEditClick = () => {
    if (shooter) {
      setEditData({
        name: shooter.name,
        nra_number: shooter.nra_number || "",
        cmp_number: shooter.cmp_number || "",
        rating: shooter.rating || "",
        caliber_group: shooter.caliber_group || "Open",
        competitor_number:
          shooter.competitor_number === 0 || shooter.competitor_number
            ? String(shooter.competitor_number)
            : "",
        division: shooter.division || "Civilian",
        special_categories: Array.isArray(shooter.special_categories)
          ? [...shooter.special_categories]
          : [],
      });
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData({
      ...editData,
      [name]: value
    });
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      setError(null);
      const token = localStorage.getItem("token");
      const payload = {
        name: editData.name.trim(),
        nra_number: editData.nra_number.trim() || null,
        cmp_number: editData.cmp_number.trim() || null,
        rating: editData.rating || null,
        caliber_group: editData.caliber_group || "Open",
        competitor_number: editData.competitor_number
          ? parseInt(editData.competitor_number, 10)
          : null,
        division: editData.division || "Civilian",
        special_categories: editData.special_categories || [],
      };
      const res = await axios.put(`${API}/shooters/${shooterId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShooter(res.data);
      setIsEditing(false);
    } catch (err) {
      console.error("Error updating shooter:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to update shooter details. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // Tab navigation
  const [activeTab, setActiveTab] = useState("overview");

  if (loading) return <div className="page text-center text-ink-500">Loading shooter details...</div>;
  if (error) return <div className="page"><div className="alert-error">{error}</div></div>;
  if (!shooter) return <div className="page text-center text-ink-500">Shooter not found</div>;

  // Helper formatting functions
  const formatCaliber = (caliber) => {
    switch(caliber) {
      case "TWENTYTWO": return ".22";
      case "CENTERFIRE": return "CF";
      case "FORTYFIVE": return ".45";
      case "NINESERVICE": return "9mm Service";
      case "FORTYFIVESERVICE": return "45 Service";
      default: return caliber;
    }
  };

  // Overview section
  const renderOverview = () => {
    return (
      <div className="card card-pad">
        <h2 className="section-title mb-4">Shooter Information</h2>
        
        {!isEditing ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="mb-4">
                <p className="text-sm text-ink-500">Name</p>
                <p className="font-medium text-ink-900">{shooter.name}</p>
              </div>
              <div className="mb-4">
                <p className="text-sm text-ink-500">NRA Number</p>
                <p className="font-medium text-ink-900">{shooter.nra_number || "Not provided"}</p>
              </div>
              <div className="mb-4">
                <p className="text-sm text-ink-500">CMP Number</p>
                <p className="font-medium text-ink-900">{shooter.cmp_number || "Not provided"}</p>
              </div>
              <div className="mb-4">
                <p className="text-sm text-ink-500">Class</p>
                <p className="font-medium text-ink-900">{shooter.rating || "Not provided"}</p>
              </div>
              <div className="mb-4">
                <p className="text-sm text-ink-500">Caliber group</p>
                <p className="font-medium text-ink-900">{shooter.caliber_group || "Open"}</p>
              </div>
              <div className="mb-4">
                <p className="text-sm text-ink-500">Competitor #</p>
                <p className="font-medium text-ink-900">{shooter.competitor_number ?? "—"}</p>
              </div>
              <div className="mb-4">
                <p className="text-sm text-ink-500">Division</p>
                <p className="font-medium text-ink-900">{shooter.division || "Civilian"}</p>
              </div>
              <div className="mb-4">
                <p className="text-sm text-ink-500">Special categories</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(shooter.special_categories || []).length === 0 ? (
                    <p className="font-medium text-ink-400">None</p>
                  ) : (
                    (shooter.special_categories || []).map((c) => (
                      <span
                        key={c}
                        className="badge-purple"
                      >
                        {c}
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>
            
            <div>
              {averages && averages.caliber_averages && Object.keys(averages.caliber_averages).length > 0 && (
                <div>
                  <p className="text-sm text-ink-500 mb-2">Average Scores by Caliber</p>
                  <div className="grid grid-cols-1 gap-3">
                    {Object.entries(averages.caliber_averages).map(([caliber, data]) => (
                      <div key={caliber} className="flex items-center justify-between p-3 rounded-lg ring-1 ring-ink-200 bg-white">
                        <div className="font-medium text-ink-900">{formatCaliber(caliber)}</div>
                        <div className="text-right">
                          <div className="font-semibold text-ink-900">{data.total_score_avg} / {data.matches_count > 0 ? (data.total_score_avg / data.matches_count).toFixed(2) : 0}</div>
                          <div className="text-xs text-ink-500">{data.matches_count} matches</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isAdmin() && (
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleEditClick}
                    className="btn-primary"
                  >
                    Edit Shooter
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <div>
                <label htmlFor="name" className="field-label">
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={editData.name}
                  onChange={handleInputChange}
                  className="input"
                  required
                />
              </div>
              <div>
                <label htmlFor="nra_number" className="field-label">
                  NRA Number
                </label>
                <input
                  id="nra_number"
                  name="nra_number"
                  type="text"
                  value={editData.nra_number}
                  onChange={handleInputChange}
                  className="input"
                />
              </div>
              <div>
                <label htmlFor="cmp_number" className="field-label">
                  CMP Number
                </label>
                <input
                  id="cmp_number"
                  name="cmp_number"
                  type="text"
                  value={editData.cmp_number}
                  onChange={handleInputChange}
                  className="input"
                />
              </div>
              <div>
                <label htmlFor="rating" className="field-label">
                  Class
                </label>
                <select
                  id="rating"
                  name="rating"
                  value={editData.rating}
                  onChange={handleInputChange}
                  className="select"
                >
                  <option value="">Select Rating</option>
                  <option value="HM">HM - High Master</option>
                  <option value="MA">MA - Master</option>
                  <option value="EX">EX - Expert</option>
                  <option value="SS">SS - Sharpshooter</option>
                  <option value="MK">MK - Marksman</option>
                  <option value="UNC">UNC - Unclassified</option>
                </select>
              </div>
              <div>
                <label htmlFor="caliber_group" className="field-label">
                  Caliber group
                </label>
                <select
                  id="caliber_group"
                  name="caliber_group"
                  value={editData.caliber_group}
                  onChange={handleInputChange}
                  className="select"
                >
                  <option value="Open">Open</option>
                  <option value="22 Only">22 Only</option>
                  <option value="Metallic">Metallic</option>
                </select>
              </div>
              <div>
                <label className="field-label">
                  Competitor #
                </label>
                <input
                  name="competitor_number"
                  type="number"
                  min="1"
                  value={editData.competitor_number}
                  onChange={handleInputChange}
                  className="input"
                />
              </div>
              <div>
                <label className="field-label">
                  Division
                </label>
                <select
                  name="division"
                  value={editData.division}
                  onChange={handleInputChange}
                  className="select"
                >
                  <option value="Civilian">Civilian</option>
                  <option value="Police">Police</option>
                  <option value="Service">Service</option>
                </select>
              </div>
            </div>

            <div className="mb-4 rounded-xl ring-1 ring-inset ring-purple-200 bg-purple-50 p-4">
              <label className="block text-sm font-semibold text-purple-900 mb-2">
                Special categories
              </label>
              <div className="flex flex-wrap gap-3">
                {SPECIAL_OPTIONS.map((opt) => (
                  <label
                    key={opt}
                    className="inline-flex items-center gap-2 bg-white ring-1 ring-inset ring-ink-200 px-3 py-2 rounded-lg cursor-pointer text-sm text-ink-700"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-brand-600"
                      checked={(editData.special_categories || []).includes(opt)}
                      onChange={() =>
                        setEditData((prev) => ({
                          ...prev,
                          special_categories: toggleSpecial(
                            prev.special_categories || [],
                            opt
                          ),
                        }))
                      }
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={saving}
                className="btn-primary"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Match History section
  const renderMatchHistory = () => {
    if (!report || !report.matches || Object.keys(report.matches).length === 0) {
      return (
        <div className="card card-pad text-center">
          <p className="text-ink-500 mb-4">This shooter has not participated in any matches.</p>
          {isAdmin() && (
            <Link to="/matches" className="btn-primary">
              Add Scores
            </Link>
          )}
        </div>
      );
    }

    // Filter matches by selected year if not "all"
    const filteredMatches = selectedYear === "all"
      ? report.matches
      : Object.fromEntries(
          Object.entries(report.matches).filter(([_, matchData]) => {
            const matchYear = new Date(matchData.match.date).getFullYear().toString();
            return matchYear === selectedYear;
          })
        );

    // Handle case when no matches exist for the selected year
    if (Object.keys(filteredMatches).length === 0) {
      return (
        <div className="card card-pad text-center">
          <p className="text-ink-500 mb-4">No matches found for the selected year ({selectedYear}).</p>
          <button
            onClick={() => setSelectedYear("all")}
            className="btn-primary"
          >
            View All Years
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {Object.entries(filteredMatches).map(([matchId, matchData]) => {
          const match = matchData.match;
          const scores = matchData.scores;
          
          return (
            <div key={matchId} className="card card-pad">
              <div className="mb-4 flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold mb-1">
                    <Link to={`/matches/${matchId}`} className="text-brand-600 hover:underline">
                      {match.name}
                    </Link>
                  </h3>
                  <div className="text-sm text-ink-600 mb-1">
                    {new Date(match.date).toLocaleDateString()} at {match.location}
                  </div>
                  {match.aggregate_type !== "None" && (
                    <div className="text-sm">
                      <span className="font-medium text-ink-700">Aggregate: </span>
                      <span className="text-brand-600">{match.aggregate_type}</span>
                    </div>
                  )}
                </div>

                <Link
                  to={`/matches/${matchId}`}
                  className="text-brand-600 hover:text-brand-700 text-sm font-medium"
                >
                  View Full Match
                </Link>
              </div>

              <div className="mt-4">
                <h4 className="font-medium text-ink-700 mb-3">Scores:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {scores.map((scoreData, index) => {
                    const { score, match_type } = scoreData;
                    if (!match_type) return null;
                    
                    return (
                      <div key={index} className="rounded-lg ring-1 ring-ink-200 p-3 bg-ink-50">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="font-medium text-ink-900">{match_type.type}</div>
                            <div className="text-sm text-ink-600">{formatCaliber(score.caliber)}</div>
                          </div>
                          {isAdmin() && (
                            <Link
                              to={`/scores/edit/${score.id}`}
                              className="text-emerald-600 hover:text-emerald-700 text-xs font-medium"
                            >
                              Edit
                            </Link>
                          )}
                        </div>
                        <div className="mt-2 flex justify-between">
                          <div>
                            <span className="text-ink-600 text-sm">Score:</span>
                            <span className="ml-1 font-semibold text-ink-900">{score.total_score === null ? "-" : score.total_score}</span>
                          </div>
                          <div>
                            <span className="text-ink-600 text-sm">X Count:</span>
                            <span className="ml-1 font-semibold text-ink-900">{score.total_x_count === null ? "-" : score.total_x_count}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Statistics section
  const renderStatistics = () => {
    // Check for report and averages
    if (!report || !report.averages || 
        (!report.averages.by_match_type || Object.keys(report.averages.by_match_type).length === 0) && 
        (!report.averages.by_caliber || Object.keys(report.averages.by_caliber).length === 0)) {
      return (
        <div className="card card-pad text-center">
          <p className="text-ink-500">Not enough match data to generate statistics.</p>
        </div>
      );
    }
    
    // Filter and calculate statistics based on the selected year
    const calculateYearlyStatistics = () => {
      if (!report || !report.matches) return null;
      
      // Get all matches for the selected year
      const yearMatches = selectedYear === "all" 
        ? report.matches 
        : Object.fromEntries(
            Object.entries(report.matches).filter(([_, matchData]) => {
              const matchYear = new Date(matchData.match.date).getFullYear().toString();
              return matchYear === selectedYear;
            })
          );
      
      if (Object.keys(yearMatches).length === 0) {
        return null; // No matches for the selected year
      }
      
      // Group scores by caliber and calculate statistics
      const caliberStats = {};
      
      Object.values(yearMatches).forEach(matchData => {
        matchData.scores.forEach(scoreData => {
          const { score, match_type } = scoreData;
          if (!score || !match_type) return;
          
          const caliber = score.caliber;
          
          // Initialize caliber stats if not exists
          if (!caliberStats[caliber]) {
            caliberStats[caliber] = {
              matches_count: 0,
              sf_score_sum: 0,
              sf_x_count_sum: 0,
              tf_score_sum: 0,
              tf_x_count_sum: 0,
              rf_score_sum: 0,
              rf_x_count_sum: 0,
              nmc_score_sum: 0,
              nmc_x_count_sum: 0,
              total_score_sum: 0,
              total_x_count_sum: 0,
              scores: []
            };
          }
          
          // Add to statistics
          caliberStats[caliber].matches_count++;
          
          // Only add to totals if the score is not null
          if (score.total_score !== null) {
            caliberStats[caliber].total_score_sum += score.total_score;
            caliberStats[caliber].total_x_count_sum += (score.total_x_count !== null ? score.total_x_count : 0);
            caliberStats[caliber].valid_matches_count = (caliberStats[caliber].valid_matches_count || 0) + 1;
          }
          
          caliberStats[caliber].scores.push(score);
          
          // Process stages
          score.stages.forEach(stage => {
            if (stage.score === null) return; // Skip null scores
            
            const x_count = stage.x_count !== null ? stage.x_count : 0;
            
            if (stage.name.includes("SF")) {
              caliberStats[caliber].sf_score_sum += stage.score;
              caliberStats[caliber].sf_x_count_sum += x_count;
              caliberStats[caliber].sf_valid_count = (caliberStats[caliber].sf_valid_count || 0) + 1;
            } else if (stage.name.includes("TF")) {
              caliberStats[caliber].tf_score_sum += stage.score;
              caliberStats[caliber].tf_x_count_sum += x_count;
              caliberStats[caliber].tf_valid_count = (caliberStats[caliber].tf_valid_count || 0) + 1;
            } else if (stage.name.includes("RF")) {
              caliberStats[caliber].rf_score_sum += stage.score;
              caliberStats[caliber].rf_x_count_sum += x_count;
              caliberStats[caliber].rf_valid_count = (caliberStats[caliber].rf_valid_count || 0) + 1;
            }
          });
          
          // Add to NMC stats if it's an NMC match
          if ((match_type.type === "NMC" || score.match_type_instance.includes("NMC")) && score.total_score !== null) {
            caliberStats[caliber].nmc_score_sum += score.total_score;
            caliberStats[caliber].nmc_x_count_sum += (score.total_x_count !== null ? score.total_x_count : 0);
            caliberStats[caliber].nmc_valid_count = (caliberStats[caliber].nmc_valid_count || 0) + 1;
          }
        });
      });
      
      // Calculate averages
      Object.keys(caliberStats).forEach(caliber => {
        const stats = caliberStats[caliber];
        const total_valid_count = stats.valid_matches_count || 0;
        const sf_valid_count = stats.sf_valid_count || 0;
        const tf_valid_count = stats.tf_valid_count || 0;
        const rf_valid_count = stats.rf_valid_count || 0;
        const nmc_valid_count = stats.nmc_valid_count || 0;
        
        if (total_valid_count > 0) {
          caliberStats[caliber] = {
            ...stats,
            matches_count: stats.matches_count,
            valid_matches_count: total_valid_count,
            sf_score_avg: sf_valid_count > 0 ? Math.round((stats.sf_score_sum / sf_valid_count) * 100) / 100 : 0,
            sf_x_count_avg: sf_valid_count > 0 ? Math.round((stats.sf_x_count_sum / sf_valid_count) * 100) / 100 : 0,
            tf_score_avg: tf_valid_count > 0 ? Math.round((stats.tf_score_sum / tf_valid_count) * 100) / 100 : 0,
            tf_x_count_avg: tf_valid_count > 0 ? Math.round((stats.tf_x_count_sum / tf_valid_count) * 100) / 100 : 0,
            rf_score_avg: rf_valid_count > 0 ? Math.round((stats.rf_score_sum / rf_valid_count) * 100) / 100 : 0,
            rf_x_count_avg: rf_valid_count > 0 ? Math.round((stats.rf_x_count_sum / rf_valid_count) * 100) / 100 : 0,
            nmc_score_avg: nmc_valid_count > 0 ? Math.round((stats.nmc_score_sum / nmc_valid_count) * 100) / 100 : 0,
            nmc_x_count_avg: nmc_valid_count > 0 ? Math.round((stats.nmc_x_count_sum / nmc_valid_count) * 100) / 100 : 0,
            total_score_avg: Math.round((stats.total_score_sum / total_valid_count) * 100) / 100,
            total_x_count_avg: Math.round((stats.total_x_count_sum / total_valid_count) * 100) / 100
          };
        }
      });
      
      return { caliber_averages: caliberStats };
    };
    
    // Use filtered stats if a specific year is selected
    const statsToUse = selectedYear !== "all" 
      ? calculateYearlyStatistics() 
      : averages;
    
    // If no stats available for the selected year
    if (selectedYear !== "all" && (!statsToUse || !statsToUse.caliber_averages || Object.keys(statsToUse.caliber_averages).length === 0)) {
      return (
        <div className="card card-pad text-center">
          <p className="text-ink-500 mb-4">No statistics available for {selectedYear}.</p>
          <button
            onClick={() => setSelectedYear("all")}
            className="btn-primary"
          >
            View All Years
          </button>
        </div>
      );
    }

    // Get caliber averages data based on selected year filter
    const statsToDisplay = selectedYear !== "all" 
      ? calculateYearlyStatistics() 
      : averages;
    
    const caliberAverages = statsToDisplay && statsToDisplay.caliber_averages ? statsToDisplay.caliber_averages : {};
    const calibers = Object.keys(caliberAverages);
    
    // Reset selected caliber tab if it's no longer available
    if (selectedCaliberTab && !calibers.includes(selectedCaliberTab) && calibers.length > 0) {
      setSelectedCaliberTab(calibers[0]);
    }

    return (
      <div className="card card-pad">
        {/* Caliber tabs */}
        {calibers.length > 0 && (
          <>
            <div className="mb-4">
              <h3 className="section-title mb-3">
                Performance by Caliber
                {selectedYear !== "all" && (
                  <span className="ml-2 text-sm font-normal text-ink-600">
                    ({selectedYear} only)
                  </span>
                )}
              </h3>
              <div className="flex flex-wrap border-b border-ink-200">
                {calibers.map((caliber) => (
                  <button
                    key={caliber}
                    onClick={() => setSelectedCaliberTab(caliber)}
                    className={`px-4 py-2 font-medium ${
                      selectedCaliberTab === caliber
                        ? "border-b-2 border-brand-600 text-brand-600"
                        : "text-ink-500 hover:text-ink-700"
                    }`}
                  >
                    {formatCaliber(caliber)}
                  </button>
                ))}
              </div>
            </div>

            {/* Selected caliber stats */}
            {selectedCaliberTab && caliberAverages[selectedCaliberTab] && (
              <div className="mt-6">
                <h4 className="font-medium text-ink-700 mb-3">
                  {formatCaliber(selectedCaliberTab)} Performance ({caliberAverages[selectedCaliberTab].matches_count} matches, {caliberAverages[selectedCaliberTab].valid_matches_count} valid)
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="rounded-lg ring-1 ring-ink-200 p-4">
                    <div className="mb-2 font-medium text-ink-900">Overall Averages</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-sm text-ink-600">Avg. Score</div>
                        <div className="font-semibold text-ink-900">{caliberAverages[selectedCaliberTab].total_score_avg}</div>
                      </div>
                      <div>
                        <div className="text-sm text-ink-600">Avg. X Count</div>
                        <div className="font-semibold text-ink-900">{caliberAverages[selectedCaliberTab].total_x_count_avg}</div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg ring-1 ring-ink-200 p-4">
                    <div className="mb-2 font-medium text-ink-900">National Match Course (NMC)</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-sm text-ink-600">Avg. Score</div>
                        <div className="font-semibold text-ink-900">{caliberAverages[selectedCaliberTab].nmc_score_avg}</div>
                      </div>
                      <div>
                        <div className="text-sm text-ink-600">Avg. X Count</div>
                        <div className="font-semibold text-ink-900">{caliberAverages[selectedCaliberTab].nmc_x_count_avg}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <h4 className="font-medium text-ink-700 mb-3">Stage Averages</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-lg ring-1 ring-ink-200 p-4">
                    <div className="mb-2 font-medium text-ink-900">Slow Fire (SF)</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-sm text-ink-600">Avg. Score</div>
                        <div className="font-semibold text-ink-900">{caliberAverages[selectedCaliberTab].sf_score_avg}</div>
                      </div>
                      <div>
                        <div className="text-sm text-ink-600">Avg. X Count</div>
                        <div className="font-semibold text-ink-900">{caliberAverages[selectedCaliberTab].sf_x_count_avg}</div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg ring-1 ring-ink-200 p-4">
                    <div className="mb-2 font-medium text-ink-900">Timed Fire (TF)</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-sm text-ink-600">Avg. Score</div>
                        <div className="font-semibold text-ink-900">{caliberAverages[selectedCaliberTab].tf_score_avg}</div>
                      </div>
                      <div>
                        <div className="text-sm text-ink-600">Avg. X Count</div>
                        <div className="font-semibold text-ink-900">{caliberAverages[selectedCaliberTab].tf_x_count_avg}</div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg ring-1 ring-ink-200 p-4">
                    <div className="mb-2 font-medium text-ink-900">Rapid Fire (RF)</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-sm text-ink-600">Avg. Score</div>
                        <div className="font-semibold text-ink-900">{caliberAverages[selectedCaliberTab].rf_score_avg}</div>
                      </div>
                      <div>
                        <div className="text-sm text-ink-600">Avg. X Count</div>
                        <div className="font-semibold text-ink-900">{caliberAverages[selectedCaliberTab].rf_x_count_avg}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {calibers.length === 0 && (
          <p className="text-ink-500 text-center">Not enough match data to generate statistics.</p>
        )}
      </div>
    );
  };

  return (
    <div className="page">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="page-title mb-2">{shooter.name}</h1>
          <div className="text-ink-600">
            {shooter.nra_number && (
              <p><span className="font-medium">NRA Number:</span> {shooter.nra_number}</p>
            )}
            {shooter.cmp_number && (
              <p><span className="font-medium">CMP Number:</span> {shooter.cmp_number}</p>
            )}
            {shooter.rating && (
              <p><span className="font-medium">Rating:</span> {shooter.rating}</p>
            )}
          </div>
        </div>

        <div className="mt-4 md:mt-0">
          <Link to="/shooters" className="btn-secondary">
            Back to Shooters
          </Link>
        </div>
      </div>
      
      <div className="mb-8">
        {/* Tabs */}
        <div className="mb-6 border-b border-ink-200">
          <div className="flex flex-wrap justify-between items-center">
            <div className="flex">
              <button
                onClick={() => setActiveTab("overview")}
                className={`px-4 py-2 font-medium text-sm ${
                  activeTab === "overview"
                    ? "border-b-2 border-brand-600 text-brand-600"
                    : "text-ink-500 hover:text-ink-700"
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab("match-history")}
                className={`px-4 py-2 font-medium text-sm ${
                  activeTab === "match-history"
                    ? "border-b-2 border-brand-600 text-brand-600"
                    : "text-ink-500 hover:text-ink-700"
                }`}
              >
                Match History
              </button>
              <button
                onClick={() => setActiveTab("statistics")}
                className={`px-4 py-2 font-medium text-sm ${
                  activeTab === "statistics"
                    ? "border-b-2 border-brand-600 text-brand-600"
                    : "text-ink-500 hover:text-ink-700"
                }`}
              >
                Statistics
              </button>
            </div>

            {/* Year Filter - Show only for Match History and Statistics tabs */}
            {(activeTab === "match-history" || activeTab === "statistics") && availableYears.length > 0 && (
              <div className="flex items-center mt-2 sm:mt-0">
                <label htmlFor="year-filter" className="mr-2 text-sm text-ink-700">
                  Year:
                </label>
                <select
                  id="year-filter"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="select text-sm"
                >
                  <option value="all">All Years</option>
                  {availableYears.map(year => (
                    <option key={year} value={year.toString()}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
        
        {/* Tab Content */}
        {activeTab === "overview" && renderOverview()}
        {activeTab === "match-history" && renderMatchHistory()}
        {activeTab === "statistics" && renderStatistics()}
      </div>
    </div>
  );
};

export default ShooterDetail;